import { motion, useReducedMotion } from "framer-motion";

/**
 * Countersign — phone mockups.
 *
 * Each variant renders a real, pixel-perfect App Store screen image inside
 * the CSS phone frame (bezel + notch). If a screen ships new in the iOS
 * build, drop the raw export at /public/screens/<slug>.webp and add it
 * to SCREENS below — no hand-drawn UI to keep in sync.
 *
 * Variants:
 *  - "home"     WHEN IT MATTERS  — the Home screen with "Someone is calling me"
 *  - "hangup"   STEP BY STEP     — "Hang up now." with "I've hung up" CTA
 *  - "safeword" ONE TAP AWAY     — Safe-word reveal (example: Purple Pancake)
 *  - "notfault" NOT YOUR FAULT   — "You did the right thing." completion
 *  - "staysafe" ALWAYS FREE      — "How to stay safe" guide
 */

const SCREENS = {
  home:     { src: "/screens/home.webp",     alt: "Countersign Home screen — 'Someone is calling me' button, 'Practise safe word', 'How to stay safe', Reminders, Change our word, Trusted contacts." },
  hangup:   { src: "/screens/hangup.webp",   alt: "Countersign Hang-up screen — 'Hang up now. You don't need to explain or be polite.' with an amber 'I've hung up' button." },
  safeword: { src: "/screens/safeword.webp", alt: "Countersign Safe-word screen — 'Your family's safe word' with the example 'Purple Pancake' and 'They said it' / 'They didn't' buttons." },
  notfault: { src: "/screens/notfault.webp", alt: "Countersign completion screen — 'You did the right thing. Hanging up and calling back on a number you already had is what stops this working. Nothing that just happened was your fault.' with an amber 'Done' button." },
  staysafe: { src: "/screens/staysafe.webp", alt: "Countersign 'How to stay safe' guide — 'None of this needs an app. Do as much or as little as you like.' Sample section: 'Tell someone, and don't feel stupid.'" },
};

export default function PhoneMockup({ variant = "safeword", label, tilt = -3, eager = false }) {
  const reduce = useReducedMotion();
  const screen = SCREENS[variant] ?? SCREENS.safeword;
  return (
    <motion.figure
      role="img"
      aria-label={label ?? screen.alt}
      className="spotlight-wrap"
      initial={reduce ? { opacity: 1, y: 0, rotate: tilt } : { opacity: 0, y: 40, rotate: tilt - 2 }}
      whileInView={{ opacity: 1, y: 0, rotate: tilt }}
      viewport={{ once: true, amount: 0.3 }}
      transition={reduce ? { duration: 0 } : { duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
      style={{ margin: 0 }}
    >
      <div className="phone-frame">
        <div className="phone-screen phone-screen--image">
          <img
            src={screen.src}
            alt=""
            aria-hidden="true"
            loading={eager ? "eager" : "lazy"}
            fetchPriority={eager ? "high" : "auto"}
            decoding="async"
            draggable="false"
            className="phone-screen-img"
          />
        </div>
      </div>
    </motion.figure>
  );
}
